import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"
import {
	IconPeopleFill18 as PeopleIcon,
	IconEnvelopePlusFill18 as SubscribeIcon,
	IconEnvelopeMinusFill18 as UnsubscribeIcon,
} from "nucleo-ui-fill-18"
import {
	IconCloudUploadOutline18 as CloudUpload,
	IconPlusOutline18 as PlusIcon,
	IconMagnifierOutline18 as Search,
} from "nucleo-ui-outline-18"
import { useDebounce } from "react-use"

import { cn } from "@productlane/lib/src/browser"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	AlertDialog,
	Avatar,
	Button,
	Empty,
	IconButton,
	Input,
	ScrollArea,
	SortableTableHeader,
	Tooltip,
	Virtualizer,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { PagedContact } from "./sorting/use-sorted-contacts"
import EmptyPeople from "@/assets/empty-states/empty-people.png"
import { MetaInfo } from "@/components/meta-info"
import { SelectionCheckbox } from "@/components/selection-checkbox"
import { SelectionPill } from "@/components/selection-pill"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useSetBackNavigation } from "@/lib/back-navigation"
import { captureError } from "@/lib/capture-error"
import {
	rowHighlightClass,
	useListRowSelection,
} from "@/lib/hooks/use-list-row-selection"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import { CustomersPageHeader } from "../customers-tabs"
import { chunkContactIds, SUBSCRIPTION_CHUNK_SIZE } from "./bulk-selection"
import { CreateContactDialog } from "./create-contact-dialog"
import { ContactsFilter } from "./filter/contacts-filter"
import { ContactsFilterControl } from "./filter/contacts-filter-control"
import { useContactsFilterState } from "./filter/contacts-filter-state"
import { useContactsFilterUrlSync } from "./filter/use-filter-url-sync"
import {
	contactEmailStatus,
	EMAIL_STATUS_ICON,
	EMAIL_STATUS_ICON_CLASS,
	EMAIL_STATUS_LABEL,
	EMAIL_STATUS_TOOLTIP,
	rowCompanies,
} from "./people-columns"
import { PeopleCompanyCell } from "./people-company-cell"
import { usePeopleOrder } from "./people-order"
import { useContactsSortState } from "./sorting/contacts-sort-state"
import { usePagedContacts } from "./sorting/use-sorted-contacts"

interface ListScroller {
	scrollToIndex: (index: number) => void
}

export const ContactsScreen = () => {
	const [isContactOpen, setIsContactOpen] = useState(false)
	const [search, setSearch] = useState("")
	const [debouncedSearch, setDebouncedSearch] = useState("")
	const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set())
	const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)
	// Kept apart from the open flag so the dialog copy holds while it closes.
	const [subscriptionTarget, setSubscriptionTarget] = useState(false)
	const [isConfirmSubscriptionOpen, setIsConfirmSubscriptionOpen] =
		useState(false)
	const [pendingSubscription, setPendingSubscription] = useState<
		boolean | null
	>(null)
	const { sortType, setSortType } = useContactsSortState()
	const navigate = useNavigate()
	const zero = useZero()
	const { role } = useRole()

	useDebounce(() => setDebouncedSearch(search), 400, [search])

	const {
		contacts: sortedContacts,
		isSettled: areContactsSettled,
		hasMore,
		loadMore,
		filters: listFilters,
		search: listSearch,
	} = usePagedContacts(debouncedSearch)
	const utils = api.useUtils()

	useContactsFilterUrlSync()
	useSetBackNavigation({ type: "people", url: "/people" })

	const visibleIds = useMemo(
		() => sortedContacts.map((contact) => contact.id),
		[sortedContacts],
	)
	useEffect(() => {
		usePeopleOrder.setState({ ids: visibleIds })
	}, [visibleIds])
	// A row that scrolled out of the filter must not stay armed for deletion.
	// While more pages exist, a select-all holds ids beyond the loaded rows.
	useEffect(() => {
		if (hasMore) return
		setSelectedIds((current) => {
			if (current.size === 0) return current
			const visible = new Set(visibleIds)
			const pruned = new Set([...current].filter((id) => visible.has(id)))
			return pruned.size === current.size ? current : pruned
		})
	}, [visibleIds, hasMore])

	const selectedCount = selectedIds.size
	const allVisibleSelected =
		visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id))

	// Rows load a page at a time, so selecting everything asks the server for the matching ids.
	const selectAll = useCallback(async () => {
		if (!hasMore) {
			setSelectedIds(new Set(visibleIds))
			return
		}
		const { ids, hasMore: cut } = await utils.contact.selectAll.fetch({
			filters: listFilters,
			search: listSearch || undefined,
		})
		setSelectedIds(new Set(ids))
		if (cut) {
			toast.info({
				title: `Selected the first ${ids.length.toLocaleString()} people`,
				icon: PeopleIcon,
			})
		}
	}, [hasMore, visibleIds, utils, listFilters, listSearch])

	const toggleAllVisible = useCallback(() => {
		if (allVisibleSelected) {
			setSelectedIds(new Set())
			return
		}
		void selectAll()
	}, [allVisibleSelected, selectAll])

	const canSelect = !!role && role !== "VIEWER" && visibleIds.length > 0
	useShortcut(
		"meta+a, ctrl+a",
		(event) => {
			if (!canSelect) return
			event.preventDefault()
			void selectAll()
		},
		{ preventDefault: true },
		[canSelect, selectAll],
	)

	const { companyIds, subscribedStates, sources, negations } =
		useContactsFilterState()
	const virtualizerRef = useRef<ListScroller | null>(null)
	const onVirtualizerUpdate = useCallback((virtualizer: ListScroller) => {
		virtualizerRef.current = virtualizer
	}, [])
	const contactIdAt = useCallback(
		(index: number) => sortedContacts[index]?.id,
		[sortedContacts],
	)
	const rows = useListRowSelection({
		count: sortedContacts.length,
		idAt: contactIdAt,
		selectedIds,
		setSelectedIds,
		onClear: () => setSelectedIds(new Set()),
		canSelect: !!role && role !== "VIEWER",
		scrollToIndex: (index) => virtualizerRef.current?.scrollToIndex(index),
		openAt: (index) => {
			const id = contactIdAt(index)
			if (id) void navigate({ to: "/people/$id", params: { id } })
		},
		resetKey: JSON.stringify([
			debouncedSearch,
			sortType,
			companyIds,
			subscribedStates,
			sources,
			negations,
		]),
	})

	const deleteSelected = useCallback(async () => {
		const count = selectedIds.size
		setIsDeleting(true)
		try {
			// Chunked so one push carries a bounded id list whatever the filter matched.
			for (const ids of chunkContactIds([...selectedIds])) {
				await mutateOk(zero.mutate(mutators.contacts.deleteMany({ ids })))
			}
			analytics.track("bulk-delete-contacts", { count })
			setSelectedIds(new Set())
			setIsConfirmDeleteOpen(false)
			toast.success({
				title: `Deleted ${count} ${count === 1 ? "person" : "people"}`,
				icon: PeopleIcon,
			})
		} catch (error) {
			captureError(error, {
				domain: "thread",
				action: "bulk delete contacts",
				extra: { count },
			})
			toast.error({
				title: `Could not delete ${count === 1 ? "this person" : "these people"}`,
				description: "Nothing was removed. Try again.",
				icon: PeopleIcon,
			})
		} finally {
			setIsDeleting(false)
		}
	}, [selectedIds, zero])

	const openSubscriptionDialog = useCallback((isSubscribed: boolean) => {
		setSubscriptionTarget(isSubscribed)
		setIsConfirmSubscriptionOpen(true)
	}, [])

	const setSelectedSubscribed = useCallback(async () => {
		const isSubscribed = subscriptionTarget
		const count = selectedIds.size
		const people = count === 1 ? "person" : "people"
		const icon = isSubscribed ? SubscribeIcon : UnsubscribeIcon
		setPendingSubscription(isSubscribed)
		try {
			await Promise.all(
				chunkContactIds([...selectedIds], SUBSCRIPTION_CHUNK_SIZE).map((ids) =>
					mutateOk(
						zero.mutate(
							mutators.contacts.setSubscribedMany({ ids, isSubscribed }),
						),
						"server",
					),
				),
			)
			analytics.track(
				isSubscribed ? "bulk-subscribe-contacts" : "bulk-unsubscribe-contacts",
				{ count },
			)
			setSelectedIds(new Set())
			setIsConfirmSubscriptionOpen(false)
			toast.success({
				title: `${isSubscribed ? "Subscribed" : "Unsubscribed"} ${count} ${people}`,
				icon,
			})
		} catch (error) {
			captureError(error, {
				domain: "thread",
				action: isSubscribed
					? "bulk subscribe contacts"
					: "bulk unsubscribe contacts",
				extra: { count },
			})
			toast.error({
				title: `Could not ${isSubscribed ? "subscribe" : "unsubscribe"} ${count === 1 ? "this person" : "these people"}`,
				description: "Try again.",
				icon,
			})
		} finally {
			setPendingSubscription(null)
		}
	}, [selectedIds, subscriptionTarget, zero])

	return (
		<>
			<MetaInfo title="People" />
			<AlertDialog
				isOpen={isConfirmDeleteOpen}
				onOpenChange={setIsConfirmDeleteOpen}
				confirmText="Delete"
				title={`Delete ${selectedCount} ${selectedCount === 1 ? "person" : "people"}?`}
				description="Their threads stay, with no contact attached."
				onCancel={() => setIsConfirmDeleteOpen(false)}
				onConfirm={deleteSelected}
			/>
			<AlertDialog
				isOpen={isConfirmSubscriptionOpen}
				onOpenChange={setIsConfirmSubscriptionOpen}
				variant="positive"
				confirmText={subscriptionTarget ? "Subscribe" : "Unsubscribe"}
				title={`${subscriptionTarget ? "Subscribe" : "Unsubscribe"} ${selectedCount} ${selectedCount === 1 ? "person" : "people"}?`}
				description={
					subscriptionTarget
						? "They get update emails again. Blocked senders stay unsubscribed."
						: "Changelog and update emails stop. Replies to their threads still go out."
				}
				onCancel={() => setIsConfirmSubscriptionOpen(false)}
				onConfirm={setSelectedSubscribed}
			/>
			<RoleCheck not={["VIEWER"]}>
				<SelectionPill
					count={selectedCount}
					noun={["person", "people"]}
					onClear={() => setSelectedIds(new Set())}
					actions={[
						{
							label: "Subscribe",
							onAction: () => openSubscriptionDialog(true),
							isLoading: pendingSubscription === true,
						},
						{
							label: "Unsubscribe",
							onAction: () => openSubscriptionDialog(false),
							isLoading: pendingSubscription === false,
							shortcut: { keys: "u", hint: "U" },
						},
						{
							label: "Delete",
							onAction: () => setIsConfirmDeleteOpen(true),
							isLoading: isDeleting,
							shortcut: {
								keys: "meta+backspace, ctrl+backspace",
								hint: `${ShortcutSymbols.CMD}${ShortcutSymbols.BACKSPACE}`,
							},
						},
					]}
				/>
			</RoleCheck>
			<div className="sticky top-0 z-1 bg-white dark:bg-gray-900">
				<div>
					<CustomersPageHeader
						actions={
							<div className="flex gap-2">
								<ContactsFilter />
								<RoleCheck not={["VIEWER"]}>
									<Tooltip label="Import customers">
										<IconButton
											variant="ghost"
											Icon={CloudUpload}
											aria-label="Import customers"
											onClick={() =>
												void navigate({ to: "/settings/contacts-csv-import" })
											}
										/>
									</Tooltip>
								</RoleCheck>

								<CreateContactDialog
									isOpen={isContactOpen}
									onOpenChange={(value) => {
										setIsContactOpen(value)
										if (!value) {
											analytics.track("close-create-contact-dialog")
										}
									}}
								/>
								<RoleCheck not={["VIEWER"]}>
									<Button
										Icon={PlusIcon}
										variant="ghost"
										onClick={() => {
											setIsContactOpen(true)
											analytics.track("open-create-contact-dialog")
										}}
									>
										Create
									</Button>
								</RoleCheck>
							</div>
						}
					/>
				</div>

				<div className="flex items-center gap-2 px-6 py-2">
					<Search className="h-4 w-4 flex-none text-secondary" />
					<Input
						variant="borderless"
						textSize="sm"
						placeholder="Find by name or email..."
						value={search}
						name="contact-search"
						className="px-0 placeholder:text-tertiary"
						inputClassName="h-7"
						onChange={(e) => {
							setSearch(e.target.value)
						}}
					/>
				</div>

				<ContactsFilterControl />
				{sortedContacts.length > 0 && (
					<div className="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-2 text-xs text-secondary dark:border-gray-700/80">
						<div className="flex min-w-40 flex-1 items-center gap-3">
							<RoleCheck not={["VIEWER"]}>
								<SelectionCheckbox
									label={
										allVisibleSelected ? "Clear selection" : "Select all people"
									}
									checked={
										allVisibleSelected
											? true
											: selectedCount > 0
												? "indeterminate"
												: false
									}
									onCheckedChange={toggleAllVisible}
								/>
							</RoleCheck>
							<SortableTableHeader
								label="Name"
								currentSortType={sortType}
								sortTypeAsc="name-asc"
								sortTypeDesc="name-desc"
								onClick={setSortType}
							/>
						</div>
						<div className="flex items-center gap-4">
							<div className="w-28 flex-none">
								<SortableTableHeader
									label="Subscribed"
									currentSortType={sortType}
									sortTypeAsc="status-asc"
									sortTypeDesc="status-desc"
									onClick={setSortType}
								/>
							</div>
							<div className="hidden w-48 flex-none lg:block">Company</div>
							<div className="hidden w-24 flex-none md:block">
								<SortableTableHeader
									label="Created"
									currentSortType={sortType}
									sortTypeAsc="oldest"
									sortTypeDesc="newest"
									onClick={setSortType}
									arrowPosition="left"
								/>
							</div>
						</div>
					</div>
				)}
			</div>

			{sortedContacts.length > 0 || !areContactsSettled ? (
				<ScrollArea
					className="min-h-0 flex-1"
					onMouseLeave={rows.onListMouseLeave}
				>
					<Virtualizer
						className="pb-2 text-sm sm:pb-20"
						itemsData={sortedContacts as PagedContact[]}
						onVirtualizerUpdate={onVirtualizerUpdate}
						itemContent={(contact, index) => {
							const status = contactEmailStatus(contact)
							const StatusIcon = EMAIL_STATUS_ICON[status]
							const contactCompanies = rowCompanies(contact)
							const isLast = index === sortedContacts.length - 1
							return (
								<>
									{isLast && hasMore && <LoadMoreOnMount onMount={loadMore} />}
									<Link
										to="/people/$id"
										params={{ id: contact.id }}
										onClick={(event) => rows.onRowClick(index, event)}
										onMouseMove={() => rows.onRowMouseMove(index)}
										className={cn(
											"flex select-none items-center justify-between gap-4 px-6 py-2.5",
											rowHighlightClass({
												isCursor: rows.cursor === index,
												isChecked: selectedIds.has(contact.id),
											}),
										)}
									>
										<div className="flex min-w-40 flex-1 items-center gap-3">
											<RoleCheck not={["VIEWER"]}>
												<SelectionCheckbox
													label={`Select ${contact.name || contact.email}`}
													checked={selectedIds.has(contact.id)}
													onCheckedChange={(checked) =>
														rows.checkAt(index, checked === true)
													}
													onShiftClick={() => rows.selectRange(index)}
												/>
											</RoleCheck>
											<Avatar
												name={contact.name || contact.email}
												image={contact.imageUrl}
												className="mr-1"
												size="s"
											/>
											<div className="flex min-w-0 items-start gap-0 truncate text-sm leading-tight sm:flex-row sm:items-center sm:gap-1.5">
												<div className="truncate font-medium">
													{contact.name}
												</div>
												<div className="hidden truncate text-secondary sm:block">
													{contact.email}
												</div>
											</div>
										</div>
										<div className="flex items-center gap-4">
											<div className="flex w-28 flex-none">
												<Tooltip
													label={EMAIL_STATUS_TOOLTIP[status]}
													shouldWrapChildren
												>
													<StatusIcon
														aria-label={EMAIL_STATUS_LABEL[status]}
														className={cn(
															"h-4 w-4 flex-none",
															EMAIL_STATUS_ICON_CLASS[status],
														)}
													/>
												</Tooltip>
											</div>
											<div className="hidden w-48 flex-none lg:block">
												<PeopleCompanyCell companies={contactCompanies} />
											</div>
											<div className="hidden w-24 flex-none text-right text-tertiary md:block">
												{format(new Date(contact.createdAt), "MMM d, yyyy")}
											</div>
										</div>
									</Link>
								</>
							)
						}}
					/>
				</ScrollArea>
			) : (
				<div className="flex min-h-0 flex-1 justify-center">
					<Empty
						text={
							debouncedSearch.trim()
								? "Try changing the search parameters"
								: "No people were found"
						}
						title={debouncedSearch.trim() ? "Nothing was found" : "People"}
						image={
							<img
								width={130}
								height={128}
								src={EmptyPeople}
								alt="Empty people"
							/>
						}
					/>
				</div>
			)}
		</>
	)
}

/** Mounts with the last row, so scrolling near the end of the loaded page fetches the next one. */
function LoadMoreOnMount({ onMount }: { onMount: () => void }) {
	useEffect(() => {
		onMount()
	}, [onMount])
	return null
}
